import { useEffect, useRef, useState } from "react";
import { chisinauDate, chisinauTime, lei } from "@/shared/format";
import {
  alertAlreadyFired, announceReady, markAlertFired, markReadyDismissed,
  primeChime, primeChimeOnFirstGesture, readyDismissed,
} from "./ready-alert";
import { statusTone } from "@/shared/status-tone";
import { AddHomeMark, ShareMark } from "@/shared/install-marks";

type Order = {
  id: string; order_number: number; pickup_code: string; status: string;
  zone_code: string | null; zone_label: string | null; guest_name: string;
  subtotal_minor: number; tip_minor: number; total_minor: number;
  payment_method: string | null;
  /*
   * WHEN, and both of them, because this is the page a bank redirects to.
   *
   * The route has always SENT `created_at` and `paid_at` — they were selected
   * from the first version of `GET /orders/:id` — and this type simply never
   * declared them, so the screen could not render what it was already being
   * given. An acquirer's integration review asks the return page to prove the
   * transaction: which order, what was in it, and WHEN IT WAS PAID.
   *
   * `paid_at` is null until money actually moves, which is why the screen falls
   * back to `created_at` and says a different word over it. A cash order handed
   * over at the bar may never have a paid timestamp at all, and printing the
   * moment it was placed under the word „plătită" would be the one thing on
   * this page that is not true.
   */
  created_at: number | null;
  paid_at: number | null;
  /**
   * Set when this order reached 'failed' because its payment window closed
   * rather than because a bank refused anything.
   *
   * The server works it out (no bank ever declined an attempt, but one expired)
   * because only the server can see the `payments` rows. Absent on a response
   * from an older Worker, which reads as false and simply keeps the old wording.
   */
  payment_expired?: boolean;
  /**
   * A Telegram chat already listens to this order (0035) — the standing
   * subscription attached it, or Start was pressed for it. True hides the
   * invitation; absent (an older Worker) reads false and keeps the offer.
   */
  tg_subscribed?: boolean;
};

/**
 * Who sold this, out of the house's own settings.
 *
 * `null` for a house that has filled none of it in — and then nothing at all is
 * drawn, rather than a heading over three empty lines. Present on both tiers of
 * the order response: it is published company information, not this guest's
 * business.
 */
type Seller = { name: string; idno: string; address: string } | null;

const T = {
  ro: {
    code: "Codul tău", showCode: "Arată codul la bar",
    awaiting_payment: "Așteptăm plata.", paid: "Am primit plata. În preparare.",
    /*
     * CELE 10 MINUTE SUNT ȚINEREA STOCULUI, NU VIAȚA COMENZII — clientul a
     * cerut textul ăsta pe 15.08.2026 și merită scris ce se întâmplă de fapt,
     * fiindcă cele două nu coincid.
     *
     * După `reservation_hold_minutes` (10 la casa asta, dar e SETARE) alarma
     * din EventRoom eliberează rezervarea. Comanda NU moare: `cancelExpiredOnBoard`
     * mută doar rândurile 'awaiting_payment', iar „Am încasat" de la bar
     * primește liniile comenzii tocmai ca să poată scădea stocul după ce
     * ținerea a expirat — worker/api/bar.ts o numește cazul NORMAL la o
     * tejghea, nu excepția: omul comandă, stă la coadă, plătește când ajunge.
     *
     * Deci propoziția grăbește oaspetele către bar, ceea ce e scopul, dar nu
     * descrie o expirare reală. Iar „10" e scris de mână aici lângă o setare
     * care poate fi schimbată din admin — dacă se schimbă, textul minte.
     */
    unpaid_cash_pending: "Pentru a finaliza comanda, apropiați-vă de bar și achitați. Aveți 10 minute.",
    preparing: "În preparare.", ready: "E gata. Te așteptăm la bar.",
    handed_over: "Ți-am dat comanda. Poftă bună!",
    failed: "Plata nu a trecut.", cancelled: "Comanda a fost anulată.",
    refunded: "Banii au fost returnați.",
    /*
     * NU „plata nu a trecut" — nimeni n-a încercat nimic.
     *
     * Pagina băncii ține un sfert de oră. Cine n-a plătit în timpul ăsta n-a
     * fost refuzat de card; i-a expirat linkul. A-i spune că plata „nu a
     * trecut" înseamnă a-i pune în cârcă un refuz care nu s-a întâmplat — și
     * omul se uită apoi în aplicația băncii după bani care n-au plecat
     * niciodată. A doua propoziție e cea care contează: nu s-a luat nimic.
     */
    expired: "A trecut timpul de plată. Nu ți-am luat niciun ban.",
    pay: "Plătește acum", total: "Total", tip: "Bacșiș", items: "Produse",
    back: "Înapoi la meniu", notFound: "Nu găsim această comandă.",
    retry: "Încearcă din nou", retryOther: "Plătește altfel",
    // The same words the checkout's method button uses — the retry screen is
    // the same choice, made again after a failure.
    retryCash: "Plătesc la bar", retryMia: "MIA", retryCard: "Card, Apple Pay, Google Pay",
    retrying: "Se pregătește…", retryFailed: "Nu am reușit. Mai încearcă o dată.",
    retryGone: "Între timp s-a terminat ceva din comandă. Spune-i barmanului.",
    retryPaid: "Comanda e deja plătită.",
    notifyAsk: "Anunță-mă când e gata",
    notifyTg: "Anunță-mă pe Telegram",
    notifyOn: "Te anunțăm când e gata.",
    notifyPage: "Te anunțăm cât timp ții pagina deschisă.",
    notifyBlocked: "Notificările sunt blocate pentru acest site. Le poți permite din bara de adresă, lângă adresă.",
    notifyNone: "Ține pagina deschisă — sună și vibrează când e gata.",
    /*
     * BANNERUL DE DUPĂ PLATĂ. Odată banii dați, singurul fir dintre bucătărie
     * și oaspete e pagina asta — iar pe un telefon fără notificări pe ecranul
     * blocat, a o închide înseamnă a nu afla niciodată că e gata. Spus TARE,
     * o singură dată pe ecran, și ascuns exact când devine fals: când
     * notificările push chiar funcționează.
     */
    keepOpen: "Ține pagina deschisă — te anunțăm aici când comanda e gata.",
    installHint: "Vrei să te anunțe și cu telefonul blocat? Din meniul de trimitere (Share), alege „Adaugă pe ecranul principal”, apoi deschide comanda de acolo.",
    // The WHY only — the HOW is the two drawn steps under it, shared with the
    // menu's walkthrough (install-marks.tsx), because prose about a button
    // nobody can picture is how the first version failed.
    installBody: "Pe iPhone, notificarea ajunge și cu ecranul blocat dacă adaugi comanda pe ecranul principal și o deschizi de acolo.",
    installStep1: "Apasă butonul de trimitere — jos în Safari, sus lângă adresă în Chrome",
    installStep2: "Derulează lista în jos și alege „Adaugă pe ecranul principal”",
    // The entry can be HIDDEN outright until enabled — found on a real phone:
    // some share sheets only reveal it after „Editați acțiunile…”. Without this
    // line, that guest concludes the option does not exist, because it doesn't.
    installMissing: "Nu apare? Apasă „Editați acțiunile…” la capătul listei și activeaz-o.",
    askTitle: "Să te anunțăm când e gata?",
    askBody: "Îți trimitem o notificare pe telefon. Poți lăsa ecranul.",
    askYes: "Da, anunță-mă", askNo: "Nu, mulțumesc",
    notifTitle: "Comanda e gata!",
    readyClose: "Am înțeles",
    cancelOrder: "Anulează comanda", cancelConfirm: "Sigur? Apasă încă o dată",
    cancelFailed: "Nu am reușit să anulăm. Mai încearcă o dată.",
    /*
     * Banca a refuzat anularea, și aproape întotdeauna dintr-un singur motiv:
     * plata tocmai a trecut. Nu spunem „n-am reușit" — omul ar apăsa din nou
     * pe o comandă deja plătită. Îi spunem ce se întâmplă și că pagina se
     * actualizează singură.
     */
    cancelPaying: "Plata e în curs. Mai așteaptă câteva secunde.",
    /*
     * ÎNTRE PLATĂ ȘI CONFIRMARE. Oaspetele tocmai s-a întors de la bancă cu
     * banii dați — să-i arăți „Plătește acum" în clipa aia înseamnă să-i spui
     * că plata n-a mers, și omul plătește a doua oară. Propoziția și rândul de
     * sub ea spun exact ce se întâmplă și ce să NU facă.
     */
    confirming: "Așteptăm confirmarea băncii…",
    confirmingHint: "Plata a fost trimisă. Nu plăti din nou — durează câteva clipe.",
    /*
     * DOVADA PLĂȚII, în cuvintele casei.
     *
     * „Comanda nr." și nu „ID": numărul e cel pe care îl caută și barul, și
     * banca, dacă cineva sună a doua zi. „Plătită la" primește data ȘI ora,
     * fiindcă într-o seară se dau zeci de comenzi și data singură nu deosebește
     * două. Când plata n-a avut loc încă — numerar nepredat, plată eșuată —
     * se scrie „Comandă plasată la", care e adevărat în toate cazurile.
     */
    orderNo: "Comanda nr.", paidAt: "Plătită la", placedAt: "Comandă plasată la",
    seller: "Vânzător", idno: "IDNO",
  },
  ru: {
    code: "Ваш код", showCode: "Покажите код в баре",
    awaiting_payment: "Ждём оплату.", paid: "Оплата получена. Заказ готовится.",
    unpaid_cash_pending: "Чтобы завершить заказ, подойдите к бару и оплатите. У вас 10 минут.",
    preparing: "Заказ готовится.", ready: "Готово. Ждём вас в баре.",
    handed_over: "Заказ выдан. Приятного аппетита!",
    failed: "Оплата не прошла.", cancelled: "Заказ отменён.",
    refunded: "Деньги возвращены.",
    expired: "Время на оплату истекло. Деньги мы не списывали.",
    pay: "Оплатить", total: "Итого", tip: "Чаевые", items: "Товары",
    back: "Назад в меню", notFound: "Не находим этот заказ.",
    retry: "Попробовать снова", retryOther: "Оплатить иначе",
    retryCash: "Плачу в баре", retryMia: "MIA", retryCard: "Карта, Apple Pay, Google Pay",
    retrying: "Готовим…", retryFailed: "Не получилось. Попробуйте ещё раз.",
    retryGone: "Что-то из заказа тем временем закончилось. Скажите бармену.",
    retryPaid: "Заказ уже оплачен.",
    notifyAsk: "Сообщить, когда будет готово",
    notifyTg: "Уведомить меня в Telegram",
    notifyOn: "Мы сообщим, когда будет готово.",
    notifyPage: "Сообщим, пока страница открыта.",
    notifyBlocked: "Уведомления для этого сайта заблокированы. Их можно разрешить в адресной строке.",
    notifyNone: "Держите страницу открытой — прозвучит сигнал, когда будет готово.",
    keepOpen: "Не закрывайте страницу — сообщим здесь, когда заказ будет готов.",
    installHint: "Хотите уведомление даже с заблокированным экраном? В меню «Поделиться» выберите «На экран „Домой“», затем откройте заказ оттуда.",
    installBody: "На iPhone уведомление придёт даже с заблокированным экраном, если добавить заказ на экран «Домой» и открыть его оттуда.",
    installStep1: "Нажмите кнопку «Поделиться» — внизу в Safari, вверху у адреса в Chrome",
    installStep2: "Прокрутите список вниз и выберите «На экран „Домой“»",
    installMissing: "Не видите? Нажмите «Изменить действия…» в конце списка и включите её.",
    askTitle: "Сообщить, когда будет готово?",
    askBody: "Пришлём уведомление на телефон. Экран можно погасить.",
    askYes: "Да, сообщите", askNo: "Нет, спасибо",
    notifTitle: "Заказ готов!",
    readyClose: "Понятно",
    cancelOrder: "Отменить заказ", cancelConfirm: "Точно? Нажмите ещё раз",
    cancelFailed: "Не получилось отменить. Попробуйте ещё раз.",
    cancelPaying: "Оплата проходит. Подождите несколько секунд.",
    confirming: "Ждём подтверждение от банка…",
    confirmingHint: "Платёж отправлен. Не платите повторно — это займёт несколько секунд.",
    orderNo: "Заказ №", paidAt: "Оплачен", placedAt: "Заказ оформлен",
    seller: "Продавец", idno: "IDNO",
  },
  en: {
    code: "Your code", showCode: "Show this code at the bar",
    awaiting_payment: "Waiting for payment.", paid: "Payment received. Being prepared.",
    unpaid_cash_pending: "To complete your order, come to the bar and pay. You have 10 minutes.",
    preparing: "Being prepared.", ready: "Ready. Come to the bar.",
    handed_over: "Handed over. Enjoy!",
    failed: "The payment didn't go through.", cancelled: "This order was cancelled.",
    refunded: "This order was refunded.",
    expired: "The payment window closed. Nothing was charged.",
    pay: "Pay now", total: "Total", tip: "Tip", items: "Items",
    back: "Back to the menu", notFound: "We can't find this order.",
    retry: "Try again", retryOther: "Pay another way",
    retryCash: "I'll pay at the bar", retryMia: "MIA", retryCard: "Card, Apple Pay, Google Pay",
    retrying: "Getting ready…", retryFailed: "That didn't work. Please try once more.",
    retryGone: "Something in the order ran out meanwhile. Tell the bartender.",
    retryPaid: "This order is already paid.",
    notifyAsk: "Notify me when it's ready",
    notifyTg: "Notify me on Telegram",
    notifyOn: "We'll let you know when it's ready.",
    notifyPage: "We'll tell you while this page stays open.",
    notifyBlocked: "Notifications are blocked for this site. You can allow them from the address bar.",
    notifyNone: "Keep this page open — it will sound when your order is ready.",
    keepOpen: "Keep this page open — we'll tell you here when your order is ready.",
    installHint: "Want the alert even with your phone locked? In the Share menu, choose “Add to Home Screen”, then open your order from there.",
    installBody: "On iPhone the alert reaches you even with the screen locked if you add this order to your Home Screen and open it from there.",
    installStep1: "Tap the Share button — bottom bar in Safari, top right in Chrome",
    installStep2: "Scroll the list down and choose “Add to Home Screen”",
    installMissing: "Don’t see it? Tap “Edit Actions…” at the end of the list and enable it.",
    askTitle: "Shall we tell you when it's ready?",
    askBody: "We'll send a notification to your phone. You can put it away.",
    askYes: "Yes, tell me", askNo: "No, thank you",
    notifTitle: "Your order is ready!",
    readyClose: "Got it",
    cancelOrder: "Cancel the order", cancelConfirm: "Sure? Tap once more",
    cancelFailed: "Couldn't cancel. Please try again.",
    cancelPaying: "Your payment is going through. Give it a few seconds.",
    confirming: "Waiting for the bank to confirm…",
    confirmingHint: "Your payment was submitted. Don't pay again — this takes a moment.",
    orderNo: "Order no.", paidAt: "Paid at", placedAt: "Order placed at",
    seller: "Seller", idno: "IDNO",
  },
} as const;

/**
 * THE STATES IN WHICH THE BAR IS ACTUALLY GOING TO MAKE THIS ORDER.
 *
 * 'awaiting_payment' IS DELIBERATELY ABSENT, and that absence is the whole
 * point of this constant. A card order that has not been paid for is not on
 * anybody's board and may never be: the checkout dies in fifteen minutes and
 * takes the order with it. Everything gated on this list is a PROMISE —
 * „Arată codul la bar", „Te anunțăm când e gata", the offer to send a
 * notification — and every one of them was being made to a guest who had not
 * paid yet. The code buys nothing at the bar, no drink is being poured, and
 * the notification would be for an event that never comes.
 *
 * 'unpaid_cash_pending' STAYS. There the code is the entire mechanism — the
 * guest walks to the counter and says it — and the bar does make the order
 * once the cash is in the till.
 *
 * One list, one name, because this was five copy-pasted literals and they had
 * already started to disagree with each other.
 */
const BEING_MADE = ["unpaid_cash_pending", "paid", "preparing"];

/**
 * THE ORDERS A GUEST MAY WALK AWAY FROM: the unpaid ones, and only those.
 *
 * Nothing here has taken the guest's money. 'awaiting_payment' owns a live card
 * checkout, 'unpaid_cash_pending' is a promise of cash at the counter, 'failed'
 * is an attempt that died — in all three the house is owed and has been given
 * nothing, so leaving costs nobody anything.
 *
 * WHAT IS DELIBERATELY ABSENT is every state past the money: paid, preparing,
 * ready, handed_over, refunded. Those are refund territory and belong to a
 * person at the bar, not to a button on a phone.
 *
 * 'awaiting_payment' IS NEW HERE and it is safe for one specific reason: the
 * cancel route now asks the BANK to take the payment page away first
 * (`POST /v2/checkouts/{id}/cancel`) and refuses to cancel anything if the bank
 * says no. The server is the authority — this list only decides whether to draw
 * the button, and the route re-checks everything.
 */
const UNPAID_STATES = ["awaiting_payment", "unpaid_cash_pending", "failed"];

/**
 * HOW LONG „AȘTEPTĂM CONFIRMAREA BĂNCII" MAY MASK THE NORMAL SCREEN.
 *
 * The signed callback was measured arriving 94 seconds after a real sandbox
 * payment (2026-08-11), so the window has to cover minutes, not seconds.
 * Three minutes is roughly twice the measurement; past it the mask lifts and
 * the ordinary awaiting/failed screen returns, because at some point "the
 * bank has not confirmed" stops being news and starts being the state the
 * guest needs to act on.
 */
const CONFIRMING_WINDOW_MS = 3 * 60 * 1000;

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** PushManager wants the applicationServerKey as bytes; the server hands base64url. */
function b64uToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function OrderStatus({ orderId, lang }: { orderId: string; lang: string }) {
  const t = T[(lang in T ? lang : "ro") as keyof typeof T];
  const [data, setData] = useState<{
    order: Order;
    items: { name_snapshot: string; qty: number; line_total_minor: number }[];
    /** Absent from a response served before the seller block existed. */
    seller?: Seller;
  } | null>(null);
  const [missing, setMissing] = useState(false);
  const [payUrl, setPayUrl] = useState<string | null>(null);
  /*
   * WHEN THE GUEST CAME BACK FROM THE BANK HAVING PAID — the moment the
   * „waiting for the bank" mask went up. In localStorage as well as state,
   * because the bank's redirect is exactly where a phone reloads the page:
   * the mask must survive a refresh, or the guest lands back on „Plătește
   * acum" with their money already gone.
   */
  const [confirmingSince, setConfirmingSince] = useState<number | null>(() => {
    const v = Number(localStorage.getItem(`prispa.confirming.${orderId}`) ?? 0);
    return v > 0 ? v : null;
  });
  /*
   * PAYING AGAIN, FROM THE ONLY SCREEN THAT STILL KNOWS ABOUT THE ORDER.
   *
   * The basket is cleared the moment an order is placed (src/guest/App.tsx), so
   * a guest whose card was declined had nothing left to check out: the order
   * existed, the house was owed, and this page offered one link back to a menu
   * with an empty cart. The order is the record now, and the items are already
   * listed below — so the retry belongs here rather than in a rebuilt basket.
   *
   * The stock was handed back when the payment failed, so this can legitimately
   * come back "something ran out". That is a real answer and it is shown as
   * one, not swallowed into "try again".
   */
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryNote, setRetryNote] = useState<string | null>(null);

  /*
   * THE MOMENT THIS PAGE EXISTS FOR IS „E GATA", and it used to arrive as
   * nothing but a quieter sentence and an olive numeral — on a phone that was
   * face-down on a table in a dark garden.
   *
   * There is deliberately no "previous status" here any more. It was the guard
   * that decided whether the moment had just happened, and it made arriving on
   * an already-ready order — from the notification, from „Comenzile mele",
   * from a refresh — the one path that announced nothing at all. What replaced
   * it is two flags in src/guest/ready-alert.ts, remembered across reloads and
   * shared with the menu: one for the noise, one for the card. See `celebrate`.
   *
   * `notify` starts from what the browser already remembers: permission
   * granted plus our own per-order flag means the guest asked for THIS order
   * and survived a reload; 'denied' hides the button for good, because asking
   * again is how products teach people to refuse notifications everywhere.
   * On iOS Safari in a plain tab `Notification` does not exist at all — the
   * button is simply never drawn, and the sound, vibration and colour still
   * carry the moment.
   */
  /*
   * FOUR STATES, BECAUSE THERE ARE FOUR — and treating this as a boolean is
   * what produced a page that offered nothing and explained nothing.
   *
   *   "off"    nothing granted yet; the ask is available.
   *   "page"   permission granted, but the LOCK-SCREEN tier is not available
   *            (no PushManager, no VAPID key on this deployment, or the
   *            subscribe was refused). We can still raise a banner while the
   *            page lives, and we must say only that.
   *   "on"     a push subscription is CONFIRMED stored on the server. Only
   *            this state may promise a notification with the tab closed.
   *   "denied" the browser is blocking us. Nothing we do can un-block it —
   *            but saying so, with where the switch is, is the difference
   *            between a feature that looks broken and one the guest can fix.
   *
   * The old code collapsed the middle two into "on" and wrote the flag BEFORE
   * the subscribe ran, so „Te anunțăm când e gata." was printed from a
   * localStorage key rather than from anything that had actually happened.
   */
  const notifySupported = "Notification" in window;
  const [notify, setNotify] = useState<"off" | "page" | "on" | "denied">(() => {
    if (!notifySupported) return "off";
    if (Notification.permission === "denied") return "denied";
    if (Notification.permission !== "granted") return "off";
    // The tier is remembered, not a bare "1": a reload must not upgrade a
    // page-only grant into a promise of lock-screen delivery.
    return localStorage.getItem(`prispa.notify.${orderId}`) === "on" ? "on" : "page";
  });
  /*
   * THE POP-UP ON THIS PAGE TOO, and the reasoning that left it out was wrong.
   *
   * The argument was that this screen IS the notification — the code turns
   * olive and the sentence changes — so a card over it would be redundant.
   * Watched happening on a real order, it is not: a line of text quietly
   * swapping for another line of text, on a page that already looked like
   * that, is not something a person notices while talking to somebody. The
   * menu got a pop-up and this page, where the guest is far more likely to be
   * waiting, got nothing.
   *
   * Same „Am înțeles" as the menu's, sharing the same remembered dismissal, so
   * acknowledging it in one place does not leave it waiting in the other.
   */
  const [popup, setPopup] = useState(false);
  /*
   * ASKING FOR NOTIFICATIONS THE MOMENT THE ORDER EXISTS, not whenever the
   * guest happens to spot a button.
   *
   * WHY THIS IS A CARD OF OURS AND NOT THE BROWSER'S OWN PROMPT FIRED ON LOAD.
   * `Notification.requestPermission()` requires a user gesture — Safari
   * rejects it outright without one and Firefox has since v72 — so a prompt
   * "on arrival" is not something the platform will give us. And it is the
   * right constraint: a native prompt with no explanation in front of it is
   * the one guests deny, and a DENIAL IS PERMANENT. There is no second ask,
   * on this site or any other page of it, ever.
   *
   * So the card is the ask, it appears immediately, and „Da" is the gesture
   * that opens the real prompt — by which point the guest already knows what
   * it is for. „Nu, mulțumesc" costs one tap, leaves the browser permission
   * untouched at 'default', and is remembered ACROSS ORDERS so the sheet is
   * not the first thing they see every time they buy a beer. The quiet
   * „Anunță-mă când e gata" button below stays available for anyone who
   * changes their mind.
   */
  const [asked, setAsked] = useState(
    () => localStorage.getItem(`prispa.notifyask.${orderId}`) === "no"
  );
  /*
   * THE iOS TWIN OF `asked`. Where the Notification API exists, the ask card
   * carries the offer; where it does not (an iPhone tab, Safari or Chrome —
   * both are WebKit), the same bottom sheet explains the one route iOS has:
   * add to the Home Screen and open from there. Apple exposes NO install
   * prompt to trigger, so the card is instructions and a single dismissal —
   * remembered per order, exactly like the notify ask.
   */
  const [installAsked, setInstallAsked] = useState(
    () => localStorage.getItem(`prispa.installask.${orderId}`) === "no"
  );
  const [cancelArmed, setCancelArmed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNote, setCancelNote] = useState<string | null>(null);

  /*
   * The table this order came from, written when it was placed.
   *
   * Null when there is nothing stored — a link forwarded to somebody else's
   * phone, or storage cleared. Then the link is not rendered at all: there is
   * no menu to go back to, and offering one that lands on "we can't find this
   * event" is worse than offering none.
   */
  const backHref = localStorage.getItem(`prispa.backto.${orderId}`);

  async function payAgain(method: "mia" | "card" | "cash") {
    setRetrying(method);
    setRetryNote(null);
    try {
      const session = localStorage.getItem(`prispa.order.${orderId}`);
      const res = await fetch(
        `/api/orders/${orderId}/pay?lang=${encodeURIComponent(lang)}` +
          (session ? `&s=${encodeURIComponent(session)}` : ""),
        { method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ payment_method: method }) }
      );
      const body = (await res.json()) as { pay_url?: string | null; error?: string };
      if (!res.ok) {
        setRetryNote(
          body.error === "insufficient_stock" ? t.retryGone
            : body.error === "already_paid" ? t.retryPaid
            : t.retryFailed
        );
        return;
      }
      // Cash has no page to open — the status below becomes the instruction.
      if (body.pay_url) {
        // localStorage, for the reason given where the first one is written
        // (src/guest/App.tsx): a bank journey does not reliably come back to
        // the same tab, and a lost pay URL strands a payable order.
        localStorage.setItem(`prispa.payurl.${orderId}`, body.pay_url);
        /*
         * MIA STAYS HERE. Its URL is the QR's own payload and the MIA API has
         * no return URL of any kind, so navigating there leaves the guest on
         * bnm.md after paying, with nothing pointing home. Showing the button
         * instead keeps this page — the one that polls and gets the callback —
         * alive underneath, and the button opens the bank in a new tab.
         *
         * A card page is a genuine round trip and still gets the old
         * behaviour: it brings the guest back to /o/<id> by itself.
         */
        if (method === "mia") {
          setPayUrl(body.pay_url);
          return;
        }
        location.href = body.pay_url;
        return;
      }
      location.reload();
    } catch {
      setRetryNote(t.retryFailed);
    } finally {
      setRetrying(null);
    }
  }

  /*
   * ONE TAP, TWO PERMISSIONS. Browsers only honour a Notification permission
   * request born from a user gesture, and the autoplay policy only unlocks an
   * AudioContext created (or resumed) inside one — so the same tap that asks
   * for the banner also warms up the chime. A page that begged on arrival
   * instead would be the page guests learn to refuse everywhere.
   */
  async function enableNotify() {
    primeChime();
    let perm: NotificationPermission;
    try {
      perm = await Notification.requestPermission();
    } catch {
      return;
    }
    if (perm !== "granted") {
      // Dismissing the prompt (X / Escape) leaves it at "default": that is not
      // a refusal and must not be recorded as one.
      setNotify(perm === "denied" ? "denied" : "off");
      return;
    }
    /*
     * The banner tier is real the moment permission is granted. The
     * lock-screen tier is a CLAIM until the server says it stored a
     * subscription, so it is set from the answer rather than in advance —
     * and the localStorage write is guarded, because an unguarded setItem
     * throwing (Safari Private Browsing) used to abort this whole function
     * AFTER the permission had already been spent.
     */
    setNotify("page");
    const subscribed = await subscribePush();
    setNotify(subscribed ? "on" : "page");
    try {
      localStorage.setItem(`prispa.notify.${orderId}`, subscribed ? "on" : "page");
    } catch {
      /* quota — the flag is only a reload convenience */
    }
  }

  /** „Da" — the gesture the native prompt needs. Either answer closes the card. */
  async function answerAsk(yes: boolean) {
    setAsked(true);
    // EITHER answer is a tap, and a tap is what the autoplay policy wants.
    // Priming on both is why a guest who declines notifications still hears
    // the chime when the drink is ready — declining the banner is not
    // declining the sound.
    primeChime();
    if (!yes) {
      /*
       * PER ORDER, NOT PER PHONE — and the difference was the whole bug.
       *
       * This key used to be global and permanent: `prispa.notifyask = "no"`.
       * One „Nu, mulțumesc", ever, on any order, and the card never appeared
       * again on that phone — leaving only the quiet button at the bottom of
       * the page, which is easy to miss and reads like a setting rather than
       * an offer. Measured on the sandbox afterwards: across every test order
       * placed, the `push_subscriptions` table held ZERO rows. Nobody had ever
       * managed to subscribe.
       *
       * Declining THIS drink is not a standing instruction about every future
       * one. Each order asks once; the browser permission is still left at
       * 'default', so nothing is burnt either way.
       */
      try {
        localStorage.setItem(`prispa.notifyask.${orderId}`, "no");
      } catch {
        /* quota — worst case they are asked again on this order */
      }
      return;
    }
    await enableNotify();
  }

  /*
   * TRUE PUSH ON TOP, so „E gata" lands even if the tab was thrown away.
   *
   * Strictly best-effort at every step — no service worker support, no
   * PushManager (iOS Safari in a plain tab), no VAPID key on this deployment,
   * a push service that refuses — each failure leaves the guest exactly where
   * the previous tier put them: the 5-second poll fires the same notification
   * (same tag, so never twice) whenever the tab still breathes. The poll is
   * the guarantee; this is the lock-screen shortcut.
   */
  async function subscribePush(): Promise<boolean> {
    /*
     * EVERY FAILURE SAYS WHICH STEP IT WAS. This function used to fail as one
     * indistinguishable `false`, which cost a full day of testing: "zero rows
     * in push_subscriptions" had five possible causes and the console named
     * none of them. A guest's phone over chrome://inspect (Android) or
     * Develop → device (iOS) now answers in one line.
     */
    const bail = (step: string, detail?: unknown): false => {
      console.warn(`[push] ${step}`, detail ?? "");
      return false;
    };
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        return bail("no PushManager on this browser (iOS Safari tab has none)");
      }

      // THE KEY FIRST. A deployment with no VAPID pair (production, until
      // go-live) can then answer "no" in one request, instead of registering a
      // worker and waiting on activation to discover there was nothing to
      // subscribe to. The route reports the pair, not just its public half.
      const keyRes = await fetch("/api/push/key");
      if (!keyRes.ok) return bail("key endpoint failed", keyRes.status);
      const { key } = (await keyRes.json()) as { key: string | null };
      if (!key) return bail("no VAPID pair on this deployment");

      /*
       * `register()` resolves as soon as the worker is INSTALLING; subscribing
       * against a registration with no active worker is the classic first-tap
       * AbortError, and it only ever bit a cold profile — i.e. a guest's first
       * order, the one time this must work. `ready` waits for activation.
       *
       * RACED AGAINST A CLOCK, because `ready` has no failure mode of its own:
       * a worker that never reaches "activated" simply never resolves it, and
       * this function used to hang there forever — no error, no state change,
       * the UI stuck on the page tier with nothing anywhere saying why. Ten
       * seconds is generous for a healthy activation; past it, the page tier
       * is the honest answer and the timeout says what happened.
       */
      await navigator.serviceWorker.register("/sw.js");
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("service worker never activated (10s)")), 10_000)
        ),
      ]);

      /*
       * A subscription minted against a DIFFERENT applicationServerKey (an
       * older deployment's pair, or another environment on the same origin)
       * makes subscribe() reject forever with InvalidStateError. The browser
       * will not replace it silently, so the stale one is dropped and the
       * subscribe retried exactly once.
       */
      const want = b64uToBytes(key) as BufferSource;
      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        const existing = sub.options?.applicationServerKey;
        const same =
          existing &&
          bytesEqual(new Uint8Array(existing), new Uint8Array(want as ArrayBuffer));
        if (!same) {
          await sub.unsubscribe().catch(() => {});
          sub = null;
        }
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want });
      }

      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        return bail("subscription came back without endpoint or keys");
      }
      const session = localStorage.getItem(`prispa.order.${orderId}`);
      if (!session) {
        // A forwarded link, cleared storage, or an incognito window: the POST
        // below would 404 by design (the session is what proves the order is
        // this guest's). Say so instead of letting the 404 pass as mystery.
        console.warn("[push] no session for this order on this device — server will refuse");
      }
      /*
       * THE ANSWER IS READ. This was `await fetch(...)` with the response
       * discarded, so 404 push_unavailable, 404 not_found (a forwarded link
       * with no session), 409 not_subscribable and 429 rate_limited all looked
       * identical to success — and the screen went on to promise a lock-screen
       * notification that no row existed to deliver.
       */
      const res = await fetch(
        `/api/orders/${orderId}/push${session ? `?s=${encodeURIComponent(session)}` : ""}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            endpoint: json.endpoint,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
            lang,
          }),
        }
      );
      if (!res.ok) bail("server refused the subscription", res.status);
      return res.ok;
    } catch (err) {
      // The page-open tier still works; the caller downgrades to "page".
      return bail("subscribe path threw", err);
    }
  }

  /*
   * Two taps on purpose, unlike everything else on this page: the first arms,
   * the second cancels, five seconds un-arms. A knee against a full-width
   * button must not erase a real order. A 409 reloads rather than complains —
   * it means the order moved (the bar took the cash after all), and the truth
   * on screen beats an error about the past.
   */
  /*
   * THE TELEGRAM OFFER — drawn only when the deployment has a bot end to end.
   * One probe on mount, the same /push/key request the push flow uses, which
   * now also names the bot (or null). Null draws nothing, exactly as a null
   * VAPID key draws no push offer.
   */
  const [tgBot, setTgBot] = useState<string | null>(null);
  useEffect(() => {
    fetch("/api/push/key")
      .then((r) => (r.ok ? r.json() : null))
      .then((b: { telegram_bot?: string | null } | null) => setTgBot(b?.telegram_bot ?? null))
      .catch(() => setTgBot(null));
  }, []);

  /*
   * THE LINK IS MINTED BEFORE THE TAP, NOT ON IT — the same Safari lesson the
   * MIA button already carries (test/payment-window.test.ts pins it): a
   * window opened after an await is popup-blocked on most of the phones at a
   * table here, and a blocked popup looks exactly like a button that does
   * nothing. So the deep link is fetched the moment the offer is drawable,
   * and what the guest taps is a real ANCHOR that navigates in-gesture.
   * A minted token nobody taps simply expires unused within the hour.
   */
  const [tgUrl, setTgUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!tgBot || tgUrl) return;
    if (!data || !BEING_MADE.includes(data.order.status)) return;
    // A chat already listening (the standing subscription, 0035) needs no
    // invitation and no token minted for it.
    if (data.order.tg_subscribed) return;
    /*
     * THE PHONE'S DURABLE KEY. Minted here, once per browser, and carried in
     * the deep link so Start can bind the chat to the PHONE rather than the
     * order — that binding is what lets every next order attach itself and
     * this screen stay quiet. Nothing about it identifies anyone until the
     * guest presses Start.
     */
    let key = localStorage.getItem("prispa.tg.key");
    if (!key) {
      key = crypto.randomUUID();
      try { localStorage.setItem("prispa.tg.key", key); } catch { /* quota */ }
    }
    const session = localStorage.getItem(`prispa.order.${orderId}`);
    fetch(
      `/api/orders/${orderId}/telegram-link?k=${encodeURIComponent(key)}${session ? `&s=${encodeURIComponent(session)}` : ""}`,
      { method: "POST" }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((b: { url?: string } | null) => setTgUrl(b?.url ?? null))
      .catch(() => {});
  }, [tgBot, tgUrl, data, orderId]);

  async function cancelOrder() {
    if (!cancelArmed) {
      setCancelArmed(true);
      setTimeout(() => setCancelArmed(false), 5000);
      return;
    }
    setCancelling(true);
    setCancelNote(null);
    try {
      const session = localStorage.getItem(`prispa.order.${orderId}`);
      const res = await fetch(
        `/api/orders/${orderId}/cancel${session ? `?s=${encodeURIComponent(session)}` : ""}`,
        { method: "POST" }
      );
      if (res.ok) {
        location.reload();
        return;
      }
      if (res.status === 409) {
        /*
         * NOT EVERY 409 IS THE SAME NEWS, and reloading on all of them buried
         * the one that matters.
         *
         * `payment_in_progress` means the BANK refused to take the payment page
         * away — which it does when the checkout has already moved on, i.e. the
         * guest has just paid. Reloading there shows „Așteptăm plata." with a
         * cancel button still under it, so the guest taps again, on an order
         * whose money is in flight. Saying what is happening and letting the
         * five-second poll land the truth is the honest answer.
         *
         * Any other 409 means the order moved for some other reason (the bar
         * took the cash, a second tab got there first), and there the reload is
         * still right: the truth on screen beats an error about the past.
         */
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        if (body?.error === "payment_in_progress") {
          setCancelArmed(false);
          setCancelNote(t.cancelPaying);
          return;
        }
        location.reload();
        return;
      }
      setCancelNote(t.cancelFailed);
    } catch {
      setCancelNote(t.cancelFailed);
    } finally {
      setCancelling(false);
    }
  }

  // Any touch on this page buys the right to make a sound when the bar
  // finishes — see primeChimeOnFirstGesture.
  useEffect(primeChimeOnFirstGesture, []);

  /*
   * ARRIVED BY TAPPING THE NOTIFICATION (public/sw.js appends ?n=1).
   *
   * The service worker closes its banner before opening this page, so the
   * shared tag has nothing to collapse into and `celebrate()` would raise a
   * second identical one — for a drink the guest is already walking towards.
   * Recording the alert as spent keeps the POP-UP (they still want the code)
   * and drops the duplicate chime and banner.
   */
  useEffect(() => {
    const url = new URL(location.href);
    if (url.searchParams.get("n") !== "1") return;
    markAlertFired(orderId);
    url.searchParams.delete("n");
    history.replaceState(null, "", url.pathname + url.search);
  }, [orderId]);

  /*
   * THE SESSION IN THE URL — kept exactly as long as the platform needs it.
   *
   * iOS gives an installed PWA a SEPARATE storage partition from Safari, so
   * `prispa.order.<id>` — the credential that proves this order is the
   * guest's — does not survive „Adaugă pe ecranul principal". The URL is the
   * only thing an install carries across, and the manifest deliberately has
   * no start_url so the installed app opens on the very page it was installed
   * from (see index.html).
   *
   * Three moves, one rule each:
   *   ARRIVING WITH ?s= and no stored session: store it. This is the
   *   installed app's first open, seeding its own partition — or a reload in
   *   Safari, where it is a harmless no-op. A session ALREADY stored wins
   *   over the URL's: it was written at checkout on this device, and a
   *   forwarded link must not overwrite it.
   *   iOS SAFARI TAB (no Notification API): make sure the URL carries the
   *   session, so an install from this page brings the order along. This is
   *   the one platform where the credential is allowed to sit in the address
   *   bar — same-origin links are the only exits from this page, and the
   *   alternative is an installed app that greets the guest as a stranger.
   *   ANYWHERE ELSE: strip it. Chrome needs no carry, and a credential does
   *   not belong in a URL longer than necessary.
   */
  useEffect(() => {
    const url = new URL(location.href);
    const carried = url.searchParams.get("s");
    const stored = localStorage.getItem(`prispa.order.${orderId}`);
    if (carried && !stored) {
      try {
        localStorage.setItem(`prispa.order.${orderId}`, carried);
      } catch {
        /* quota — the URL keeps carrying it */
      }
    }
    const session = stored ?? carried;
    if (!notifySupported && session) {
      if (carried !== session) {
        url.searchParams.set("s", session);
        history.replaceState(null, "", url.pathname + url.search);
      }
    } else if (carried) {
      url.searchParams.delete("s");
      history.replaceState(null, "", url.pathname + url.search);
    }
  }, [orderId, notifySupported]);

  /*
   * THE RETURNING GUEST, WHO HAD ALREADY SAID YES.
   *
   * Permission is per-ORIGIN and permanent; the push subscription is per
   * ORDER. So a guest who granted on their first drink came back for a second
   * and got: no ask card (it wants 'default'), a button reading „Anunță-mă
   * când e gata" as though they had never agreed, and — until they noticed and
   * tapped it — NO push subscription row for the new order at all. The one
   * tier they had explicitly opted into was the one silently missing, for the
   * commonest case at an event: the second round.
   *
   * No gesture is needed here: `subscribe()` only demands one while the
   * permission is still 'default'. Guarded by a ref rather than the stored
   * flag, so every visit re-asserts the subscription — the server's upsert
   * refreshes `created_at`, which its keep-the-newest-five prune depends on.
   */
  const autoSubbed = useRef(false);
  useEffect(() => {
    autoSubbed.current = false;
  }, [orderId]);

  /*
   * The returning guest is re-subscribed only once the order is real — see
   * BEING_MADE. An unpaid card order that expires would otherwise leave a
   * push_subscriptions row for a drink nobody is making. Nothing is lost by
   * waiting: `pendingStatus` is a dependency of the effect below, so the
   * subscribe fires on the very poll that sees the payment land.
   */
  const pendingStatus = !!data && BEING_MADE.includes(data.order.status);

  useEffect(() => {
    if (autoSubbed.current || !notifySupported || !pendingStatus) return;
    if (Notification.permission !== "granted") return;
    autoSubbed.current = true;
    void (async () => {
      const ok = await subscribePush();
      setNotify(ok ? "on" : "page");
      try {
        localStorage.setItem(`prispa.notify.${orderId}`, ok ? "on" : "page");
      } catch {
        /* quota */
      }
    })();
    // subscribePush is stable enough for this one-shot; the ref is the guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, notifySupported, pendingStatus]);

  useEffect(() => {
    setPayUrl(localStorage.getItem(`prispa.payurl.${orderId}`));

    let alive = true;
    let timer: number | null = null;
    // Requests can resolve out of order — the 5s tick racing the
    // visibilitychange refresh on one bar of signal — and a stale response
    // applied late would walk the screen back from „E gata" and make the next
    // poll celebrate the same transition twice. Only the newest request writes.
    let seq = 0;
    const tt = T[(lang in T ? lang : "ro") as keyof typeof T];

    /*
     * „E GATA" — TWO INDEPENDENT QUESTIONS, AND CONFLATING THEM HID THE POP-UP.
     *
     * This used to fire only on a TRANSITION: a poll returning 'ready' where
     * the previous poll had said something else. That is the right rule for
     * the NOISE and the wrong one for the CARD, and the difference is exactly
     * what a guest sees when they open their order from the push notification,
     * from „Comenzile mele", or by pulling the page down to refresh — every
     * one of those arrives with no previous status to differ from, so the
     * order was already ready, and the screen greeted them with a changed
     * sentence and nothing else. Which is the complaint that started this.
     *
     * So they are asked separately, the same way the menu asks them
     * (src/guest/App.tsx):
     *
     *   THE NOISE — once per order, ever, on this phone. `alertAlreadyFired`
     *   is remembered across reloads and shared with the menu, so a guest
     *   walking to the bar with the page open in two places is chimed at once.
     *
     *   THE POP-UP — until it is acknowledged, however the guest got here. It
     *   is the pickup code in the largest type on the screen; a person who has
     *   not yet dismissed it has not yet been told.
     */
    const celebrate = (code: string) => {
      if (!alertAlreadyFired(orderId)) {
        markAlertFired(orderId);
        announceReady({
          orderId,
          pickupCode: code,
          title: tt.notifTitle,
          body: `${code} · ${tt.ready}`,
        });
      }
      if (!readyDismissed(orderId)) setPopup(true);
    };

    const load = async () => {
      const mySeq = ++seq;
      try {
        // The session proves the order is this guest's. Without it the server
        // answers only "is it ready" and no money at all — see the note on the
        // route in worker/api/orders.ts.
        const session = localStorage.getItem(`prispa.order.${orderId}`);
        const r = await fetch(
          `/api/orders/${orderId}${session ? `?s=${encodeURIComponent(session)}` : ""}`
        );
        /*
         * ONLY A 404 MEANS "NOT FOUND", and this treated every non-200 as one.
         *
         * The latch was permanent — nothing ever set `missing` back — and the
         * render checks it before it checks whether there is data, so one
         * unlucky response replaced a live order screen with "Nu găsim această
         * comandă" for good. The realistic trigger is the worst one: the guest
         * is redirected back from the bank onto this page, the first poll lands
         * on a 429 from the rate limiter or a 5xx during a deploy, and the
         * person who has just paid is told their order does not exist. They
         * have no order number on screen to show anyone at the bar.
         *
         * Anything else — 429, 500, a dropped connection — keeps whatever is
         * already on screen and tries again in five seconds.
         */
        if (r.status === 404) {
          if (alive && mySeq === seq) setMissing(true);
          return;
        }
        if (!r.ok) return;
        const body = (await r.json()) as typeof data;
        if (alive && mySeq === seq) {
          setMissing(false);
          setData(body);

          const status = body?.order.status;
          if (status) {
            // No transition test — see the note on `celebrate`. Arriving on an
            // already-ready order is the commonest way to reach this screen,
            // and it was the one way that announced nothing.
            if (status === "ready") celebrate(body.order.pickup_code);
            // The tab strip is readable without unlocking a thing.
            if (status === "ready") {
              document.title = `${body.order.pickup_code} · ${tt.notifTitle}`;
            }

            /*
             * THE STASHED BANK PAGE DIES WITH THE STATE THAT MADE IT VALID.
             *
             * It is kept in localStorage so „Plătește acum" survives a
             * refresh, the banking app, and a tab that was thrown away — but
             * that same durability is why it has to be cleared deliberately.
             * A pay URL offered on an order that is already paid, retried onto
             * a new attempt, cancelled or dead is at best a page the bank will
             * refuse and at worst an invitation to pay twice.
             *
             * 'awaiting_payment' is the ONLY state it means anything in — the
             * same condition the button itself renders under, so the two can
             * never disagree.
             */
            if (status !== "awaiting_payment") {
              localStorage.removeItem(`prispa.payurl.${orderId}`);
              setPayUrl(null);
            }
            /*
             * WHILE THE „WAITING FOR THE BANK" MASK IS UP, KEEP ASKING.
             *
             * The one-shot reconcile on arrival loses to a bank that settles
             * on its own clock — measured: the status flipped somewhere inside
             * a 94-second window. So while the mask is earned, every other
             * five-second poll fires another reconcile (~10s cadence, well
             * inside the route's 20/min budget), and the first "paid" answer
             * ends the wait. The poll itself is the clock; no extra timer.
             */
            if (
              (status === "awaiting_payment" || status === "failed") &&
              confirmingFresh() &&
              Date.now() - lastReconcile.current > 9_000
            ) {
              lastReconcile.current = Date.now();
              void fireReconcile();
            }
            // The wait ended — paid, cancelled, whatever the truth turned out
            // to be. The flag must not resurface on a later visit.
            if (status && status !== "awaiting_payment" && status !== "failed") {
              try {
                localStorage.removeItem(`prispa.confirming.${orderId}`);
              } catch {
                /* quota */
              }
            }
            // Nothing after these three ever changes again; asking every five
            // seconds forever was only ever burning the guest's battery.
            if (
              (status === "handed_over" || status === "cancelled" || status === "refunded") &&
              timer !== null
            ) {
              clearInterval(timer);
              timer = null;
            }
          }
        }
      } catch {
        /* offline — keep showing whatever we already have */
      }
    };

    /*
     * THE RETURN FROM THE BANK IS A TRIGGER, NEVER A FACT.
     *
     * maib bounces the guest back with `checkoutId`/`checkoutStatus` on the
     * URL. Unsigned, so nothing on this screen believes them — but they mean
     * the bank has an answer RIGHT NOW, and the signed callback has been
     * measured arriving 94 seconds later. One POST to the reconcile route asks
     * the bank over the authenticated channel and books a paid answer through
     * the same idempotent path the callback uses; the guest sees „plătit" on
     * the very first load instead of after a minute of „Plătește acum".
     *
     * The parameters are scrubbed afterwards so a refresh, a share or a
     * bookmark does not re-ask the bank — the five-second poll is the steady
     * state, this is the one-shot shortcut.
     */
    const params = new URLSearchParams(location.search);
    /*
     * THE TWO RAILS BOUNCE THE GUEST BACK WITH DIFFERENT PARAMETERS, and
     * reading only Checkout's meant the v1 route got none of this screen's
     * shortcuts — no reconcile, no mask. A guest who had just paid by card
     * landed on „Așteptăm plata" under a „Plătește acum" button and was
     * invited to pay a second time. Observed on order 33.
     *
     *   Checkout v2  `checkoutId` + `checkoutStatus`
     *   e-commerce v1  `payId` + `orderId`  (maib's docs: "GET: okUrl + payId&orderId")
     *
     * Matched case-insensitively because the only thing worse than reading the
     * wrong parameter is reading the right one in the wrong case and never
     * finding out.
     */
    const hasParam = (name: string) => {
      const want = name.toLowerCase();
      for (const k of params.keys()) if (k.toLowerCase() === want) return true;
      return false;
    };
    const returnedFromBank =
      params.has("checkoutStatus") || params.has("checkoutId") || hasParam("payId");
    const fireReconcile = async () => {
      try {
        const session = localStorage.getItem(`prispa.order.${orderId}`);
        if (session) {
          await fetch(
            `/api/orders/${orderId}/reconcile?s=${encodeURIComponent(session)}`,
            { method: "POST" }
          );
        }
      } catch {
        /* the poll and the callback remain the safety net */
      }
    };
    /*
     * Is the „waiting for the bank" mask still earned? Read from localStorage
     * rather than state, because this closure outlives renders and must not
     * keep asking the bank on a stale flag.
     */
    const confirmingFresh = () => {
      const v = Number(localStorage.getItem(`prispa.confirming.${orderId}`) ?? 0);
      return v > 0 && Date.now() - v < CONFIRMING_WINDOW_MS;
    };
    const lastReconcile = { current: 0 };
    if (returnedFromBank) {
      /*
       * ONLY "Completed" RAISES THE MASK. The parameter is unsigned, so it
       * moves no money state — but choosing a SENTENCE on its word is safe,
       * and the sentence matters: a guest bounced back after paying must see
       * „așteptăm confirmarea băncii", not a pay button that reads as "your
       * payment did not happen". A Failed/Expired return keeps the ordinary
       * screen, where retry and cancel are the right offers.
       */
      /*
       * ON v1 THE RETURN DOES NOT SAY WHETHER IT WORKED, and the mask goes up
       * anyway. worker/payments/maib-v1.ts sends the SAME url as okUrl and
       * failUrl on purpose — a redirect is a hint about where the guest went,
       * never evidence that money moved — so a v1 bounce carries `payId` and
       * nothing that distinguishes paid from declined.
       *
       * Raising it regardless is the right trade, because the two mistakes are
       * not the same size. A guest who PAID and is shown „Plătește acum" may
       * pay twice; a guest who was DECLINED and is shown „așteptăm confirmarea
       * băncii" waits about a second for the reconcile below to come back with
       * the truth, and then gets the retry. One costs money, the other costs a
       * heartbeat — and the sentence is honest either way, because we really
       * are waiting for the bank at that moment.
       */
      const v1Return = hasParam("payId") && !params.has("checkoutStatus");
      if (params.get("checkoutStatus") === "Completed" || v1Return) {
        const at = Date.now();
        try {
          localStorage.setItem(`prispa.confirming.${orderId}`, String(at));
        } catch {
          /* quota — state alone still masks until the next reload */
        }
        setConfirmingSince(at);
      }
      lastReconcile.current = Date.now();
      void fireReconcile()
        .finally(() => {
          // Scrubbed so a refresh or a shared link does not re-ask the bank.
          history.replaceState(null, "", location.pathname + location.hash);
        })
        .then(load);
    } else {
      load();
    }
    // Poll rather than push: the guest's phone may sleep, and a WebSocket that
    // silently died would leave them staring at a stale screen.
    timer = window.setInterval(() => void load(), 5000);
    // The moment the phone is unlocked is the moment the answer is most likely
    // to have changed — same pattern as the menu's availability poll.
    const onVisible = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      if (timer !== null) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [orderId, lang]);

  if (missing) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-linen px-6 text-center">
        <div>
          <p className="text-ink-muted">{t.notFound}</p>
          {backHref && (
            <a href={backHref} className="mt-4 inline-block text-clay-600 underline">{t.back}</a>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-dvh items-center justify-center bg-linen"><p className="text-ink-faint">…</p></div>;
  }

  const { order, items, seller } = data;
  /*
   * ONE STATUS, TWO SENTENCES. 'failed' covers a bank that said no and a guest
   * who never reached the bank at all, and only the server can tell them apart
   * (it can see the payment attempts; this page cannot). When it says the
   * window closed, that is what gets printed — the retry buttons and the cancel
   * button below are drawn from the STATUS and so appear either way.
   */
  const words = T[(lang in T ? lang : "ro") as keyof typeof T] as Record<string, string>;
  /*
   * THE MASK BETWEEN THE GUEST'S MONEY AND THE BANK'S WORD. While it is up,
   * the pay button, the retry buttons and the cancel button are all withheld —
   * every one of them reads as "your payment did not happen" to somebody whose
   * payment very much did. It lifts on its own: the moment the status moves,
   * or when the window runs out (each render re-reads the clock, and the
   * five-second poll keeps renders coming).
   */
  const confirming =
    confirmingSince !== null &&
    Date.now() - confirmingSince < CONFIRMING_WINDOW_MS &&
    (order.status === "awaiting_payment" || order.status === "failed");
  const message = confirming
    ? words.confirming
    : ((order.payment_expired ? words.expired : words[order.status]) ?? order.status);
  const done = order.status === "ready";

  /*
   * WHAT THE NOTIFICATION AREA SAYS, decided once, so the card and the line
   * below it cannot both appear (they used to, for every first-time guest:
   * the same offer twice, one loud and one quiet).
   *
   * `livePermission` is read on every render rather than captured at mount.
   * The page re-renders every five seconds from the poll, so a guest who
   * unblocks the site in browser settings watches this correct itself without
   * knowing they were supposed to reload.
   */
  const livePermission: NotificationPermission | "unsupported" = notifySupported
    ? Notification.permission
    : "unsupported";
  const stillComing = BEING_MADE.includes(order.status);
  /*
   * „ȚINE PAGINA DESCHISĂ" — LOUD, once the money is in.
   *
   * After payment the only thread between the kitchen and the guest is this
   * page. On a phone without lock-screen push, closing it means never
   * learning the drink is ready — so the instruction is a bordered banner,
   * not small print. One gate: NOT for 'unpaid_cash_pending' — that guest's
   * instruction is the code at the counter, and they become 'paid' the moment
   * the bar takes the cash.
   *
   * IT DOES NOT YIELD TO WORKING PUSH, and it did at first — hidden whenever
   * `notify === "on"`, on the argument that a warning that is sometimes false
   * teaches people to ignore it. The owner overruled it, and the argument for
   * the gate was weaker than it looked: a subscription that verified once is
   * not delivery (iOS PWAs and battery savers drop pushes without a word),
   * and the banner's own sentence — we announce it HERE — stays true with
   * push on. The quiet notifyOn line yields to the banner instead.
   */
  const keepOpenLoud = stillComing && order.status !== "unpaid_cash_pending";
  const askVisible =
    stillComing && !asked && !popup && livePermission === "default";

  const notifyLine = !stillComing
    ? null
    : livePermission === "unsupported"
      ? (
          /*
           * iOS Safari in a plain tab has no Notification API at all — no
           * amount of code here changes that. What changed the answer is the
           * manifest (index.html): INSTALLED, this same app on iOS 16.4+ has
           * real Web Push, so under the honest „ține pagina deschisă" line
           * the page now offers the way there. Only while the URL carries
           * the session — the effect above keeps it there on exactly this
           * platform — because an install without it opens a storage
           * partition that has never heard of this order, and an app that
           * greets the guest as a stranger is worse than no invitation.
           */
          <div>
            {/* The banner above already says „keep it open" loudly; repeating
                it here in small print would be the same sentence twice. */}
            {!keepOpenLoud && (
              <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{t.notifyNone}</p>
            )}
            {new URLSearchParams(location.search).has("s") && (
              <p className="mt-2 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                {t.installHint}
              </p>
            )}
          </div>
        )
      : livePermission === "denied"
        ? (
            <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
              {t.notifyBlocked}
            </p>
          )
        : notify === "on"
          ? // The banner above already promises the announcement; a second,
            // quieter promise underneath would just be the same sentence.
            keepOpenLoud ? null : (
              <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>{t.notifyOn}</p>
            )
          : notify === "page"
            ? // Granted, but no lock-screen delivery: no PushManager, no
              // VAPID pair on this deployment, or the server refused the
              // subscription. Promise only what will actually happen — and
              // when the loud banner is up, it has promised exactly this.
              keepOpenLoud ? null : (
                <p className="text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                  {t.notifyPage}
                </p>
              )
            : (
                <button
                  onClick={() => void enableNotify()}
                  className="tap w-full border border-dove-400 bg-paper px-6 text-ink-muted"
                  style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
                >
                  {t.notifyAsk}
                </button>
              );

  return (
    <div className="min-h-dvh bg-linen px-5 py-10">
      {/*
        * „COMANDA TA E GATA" — over the page, not merely on it.
        *
        * The same bottom sheet the menu draws, deliberately: a guest who was
        * reading the menu and one who was watching this screen must be told
        * in the same voice, and „Am înțeles" is remembered across both.
        *
        * No „Vezi comanda" button here — they are looking at it. The code is
        * repeated because the whole errand is walking to the bar and saying it,
        * and the pop-up may be the only part they read.
        */}
      {/*
        * THE ASK, FIRST THING, WHILE THE ORDER IS STILL AHEAD OF THEM.
        *
        * Conditions, each of them load-bearing:
        *   `notifySupported` — iOS Safari in a plain tab has no Notification
        *     API at all; there the sound, the vibration and the pop-up are the
        *     whole feature and a card offering something impossible would be a
        *     lie.
        *   `permission === "default"` — never re-ask someone who has already
        *     granted (nothing to gain) or denied (nothing we can do).
        *   `!asked` — one ask per phone, remembered across orders.
        *   a PENDING status — an order already ready, handed over or cancelled
        *     has nothing left to announce.
        *   `!popup` — one bottom sheet at a time; if the drink is ready the
        *     news wins over the offer to send news.
        */}
      {notifySupported &&
        !asked &&
        !popup &&
        Notification.permission === "default" &&
        BEING_MADE.includes(order.status) && (
          <div
            className="fixed inset-x-0 bottom-0 z-40 border-t border-dove-200 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.15)]"
            style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="mx-auto max-w-md">
              <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
                {t.askTitle}
              </p>
              <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                {t.askBody}
              </p>
              <button
                onClick={() => void answerAsk(true)}
                className="tap mt-4 w-full bg-olive-500 px-6 text-paper"
                style={{ borderRadius: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
              >
                {t.askYes}
              </button>
              {/* An equal choice, styled as one — the same rule the pairing
                  suggestion follows for „Nu, mulțumesc". */}
              <button
                onClick={() => void answerAsk(false)}
                className="tap mt-2 w-full px-6 text-ink-faint"
                style={{ borderRadius: 0 }}
              >
                {t.askNo}
              </button>
            </div>
          </div>
        )}

      {/*
        * THE SAME ASK, ON THE PLATFORM THAT NEEDS AN EXTRA STEP FIRST.
        *
        * An iPhone tab (Safari or Chrome — both WebKit) has no Notification
        * API, so the card above can never render there — and Apple offers no
        * install prompt for a page to trigger. What a page CAN do is say the
        * one true thing: add this to the Home Screen and the notification
        * works. Same bottom sheet, same title, once per order, one dismissal.
        *
        * Gated on the URL still carrying the session (`?s=`), because the
        * install snapshots the URL and an installed app without the session
        * would greet the guest as a stranger — inviting that would be worse
        * than staying quiet. The effect above keeps `?s=` present on exactly
        * this platform, so in practice the gate only skips forwarded links.
        */}
      {!notifySupported &&
        !installAsked &&
        !popup &&
        new URLSearchParams(location.search).has("s") &&
        BEING_MADE.includes(order.status) && (
          <div
            className="fixed inset-x-0 bottom-0 z-40 border-t border-dove-200 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.15)]"
            style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="mx-auto max-w-md">
              <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}>
                {t.askTitle}
              </p>
              <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                {t.installBody}
              </p>
              {/* The HOW, drawn: the guest matches the glyph in the row to
                  the one on their toolbar, which words alone never managed. */}
              <div className="mt-3 flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-dove-300 text-ink">
                  <ShareMark />
                </span>
                <p className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>1 · {t.installStep1}</p>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-dove-300 text-ink">
                  <AddHomeMark />
                </span>
                <p className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>2 · {t.installStep2}</p>
              </div>
              <p className="mt-2 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
                {t.installMissing}
              </p>
              <button
                onClick={() => {
                  // The tap doubles as the chime's unlock, like every other
                  // dismissal on this page — declining the sheet is not
                  // declining the sound.
                  primeChime();
                  setInstallAsked(true);
                  try {
                    localStorage.setItem(`prispa.installask.${orderId}`, "no");
                  } catch {
                    /* quota — worst case the sheet returns on reload */
                  }
                }}
                className="tap mt-4 w-full border border-dove-400 bg-paper px-6 text-ink-muted"
                style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
              >
                {t.readyClose}
              </button>
            </div>
          </div>
        )}

      {popup && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-olive-500 bg-paper p-5 shadow-[0_-2px_40px_-10px_rgba(83,54,36,0.25)]"
          style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto flex max-w-md items-center gap-4">
            <div className="min-w-0 flex-1">
              <p
                className="text-olive-600"
                style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)" }}
              >
                {t.notifTitle}
              </p>
              <p className="mt-1 text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
                {t.ready}
              </p>
            </div>
            <span
              className="tabular shrink-0 text-olive-600"
              style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", lineHeight: 1 }}
            >
              {order.pickup_code}
            </span>
          </div>
          <button
            onClick={() => {
              markReadyDismissed(orderId);
              setPopup(false);
            }}
            className="tap mx-auto mt-4 block w-full max-w-md border border-dove-400 bg-paper px-6 text-ink-muted"
            style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
          >
            {t.readyClose}
          </button>
        </div>
      )}

      <div className="mx-auto max-w-md text-center">
        <p className="uppercase text-ink-faint" style={{ fontSize: "var(--text-kicker)", letterSpacing: "0.1em" }}>
          {t.code}
        </p>

        {/* The one big thing on the screen. Readable across a dark garden. */}
        <p
          className="tabular mt-2"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-hero)",
            lineHeight: 1,
            color: done ? "var(--color-olive-500)" : "var(--color-ink)",
          }}
        >
          {order.pickup_code}
        </p>

        {/* Ready is still the one state worth shouting — display size and the
            same olive the code just turned — but every state now wears its
            color (status-tone.ts), so „așteaptă plata" and „predată" stop
            reading as the same grey sentence with different words. */}
        <p
          className="mt-4"
          style={{
            fontSize: done ? "var(--text-xl)" : "var(--text-lg)",
            fontFamily: done ? "var(--font-display)" : undefined,
            // While confirming, never the 'failed' red — the guest has done
            // nothing wrong and nothing has failed; the bank is just slow.
            color: statusTone(confirming ? "awaiting_payment" : order.status),
          }}
        >
          {message}
        </p>

        {confirming && (
          <div className="mt-3 flex items-center justify-center gap-3" role="status" aria-live="polite">
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-dove-400"
              style={{ borderTopColor: "var(--color-clay-500)" }}
            />
            <p className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
              {t.confirmingHint}
            </p>
          </div>
        )}

        {/* The loud „ține pagina deschisă" — see keepOpenLoud above for the
            two gates. Bordered in the olive the ready state will wear, so
            the promise and its fulfilment share a colour. */}
        {keepOpenLoud && (
          <div
            role="status"
            className="mt-6 border-2 border-olive-500 bg-paper px-5 py-4"
            style={{ borderRadius: 0 }}
          >
            <p
              className="text-olive-600"
              style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-base)" }}
            >
              {t.keepOpen}
            </p>
          </div>
        )}
        {/*
          * WHO ORDERED — AND NO LONGER FROM WHERE. Owner's request, the second
          * half of taking the QR name off the guest screens (the first was the
          * checkout's pre-filled name box). The zone still rides the order row
          * and the STAFF board still prints it — the runner is who the code
          * was minted for. The guest's own screen now names only the guest.
          *
          * A name that IS the place — an order placed while the box still
          * pre-filled, or the nameless-order fallback at submit — is the QR
          * word wearing a name's clothes, so it renders as nothing rather
          * than sneaking the place back onto the screen.
          */}
        {(() => {
          const place = order.zone_label ?? order.zone_code;
          const name = order.guest_name?.trim();
          if (!name || name === place) return null;
          return (
            <p className="mt-1 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
              {name}
            </p>
          );
        })()}

        {/*
          * „ARATĂ CODUL LA BAR" IS AN INSTRUCTION FOR CASH, not a caption for
          * the number above it. On 'unpaid_cash_pending' saying the code at
          * the counter is literally how the order gets paid and found. On
          * 'awaiting_payment' — the card guest, mid-redirect — the code opens
          * nothing: the bar cannot take their money and has not started their
          * drink. Sending them to the counter with it wastes a walk and a
          * bartender's attention on an order that may expire unpaid.
          */}
        {order.status === "unpaid_cash_pending" && (
          <p className="mt-4 text-ink-muted">{t.showCode}</p>
        )}

        {/*
          * „ANUNȚĂ-MĂ CÂND E GATA" — a button, never an automatic prompt.
          *
          * Drawn only while the order can still BECOME ready, only where the
          * Notification API exists (iOS Safari in a plain tab has none — the
          * chime, vibration and colour still carry the moment there), and
          * never again once refused: nagging cannot un-refuse a permission,
          * it can only teach the guest to refuse faster.
          */}
        {/*
          * ONE BLOCK, FOUR ANSWERS, AND NEVER SILENCE.
          *
          * Every branch below says something. The state that started this —
          * permission 'denied', which is where any guest lands who once tapped
          * Block, and where Chrome puts people automatically after a few
          * dismissals — used to render NOTHING AT ALL: no card, no button, no
          * sentence. The feature looked missing rather than blocked, and the
          * one thing that would have fixed it (the switch in the address bar)
          * was the one thing nobody was told.
          *
          * The permission is read LIVE rather than from mount state, so a
          * guest who follows the instruction and flips the switch sees the
          * page correct itself on the next five-second poll, with no reload.
          */}
        {askVisible ? null : notifyLine === null ? null : (
          <div className="mt-6">{notifyLine}</div>
        )}

        {/* The second ear. A chat survives everything an iOS tab does not, so
            the offer stands beside the browser one while the order is still
            coming — same words as the ready push, spoken by the bot. Drawn
            independently of the push ask's state: the two channels are
            siblings, not stages of one flow. */}
        {stillComing && !order.tg_subscribed && tgUrl && (
          <a
            href={tgUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tap mt-2 inline-flex w-full items-center justify-center border border-dove-400 bg-paper px-6 text-ink-muted"
            style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
          >
            {t.notifyTg}
          </a>
        )}

        {payUrl && order.status === "awaiting_payment" && !confirming && (
          <a
            href={payUrl}
            /*
             * A NEW TAB FOR MIA, THE SAME TAB FOR A CARD.
             *
             * The card page redirects the payer back here when it is done, so
             * replacing this tab loses nothing. MIA never comes back — its URL
             * is the QR payload and the API has no return URL — so this tab is
             * the guest's only way home, and it has to survive. It polls while
             * they are away and is already showing „am primit plata" when they
             * switch back.
             *
             * A LINK RATHER THAN window.open, deliberately: opening a window
             * after an await is popup-blocked on Safari, which is most of the
             * phones at a table in Moldova. A tap on an anchor is never blocked.
             */
            {...(order.payment_method === "mia"
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className="tap mt-6 flex items-center justify-center gap-3 bg-clay-500 px-6 text-paper"
            style={{ borderRadius: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
          >
            {t.pay}
            {/* Same chevron, same weight, as the two bottom bars — this leaves
                for the bank's page exactly as the checkout's button does. */}
            <svg
              width="20" height="20" viewBox="0 0 24 24" fill="none"
              aria-hidden="true" focusable="false" className="shrink-0"
            >
              <path
                d="M9 5l7 7-7 7"
                stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </a>
        )}

        {/*
          * THE WAY OUT OF A DECLINE.
          *
          * Only on 'failed', which is the one state the server will retry from.
          * Card first for the same reason the checkout preselects it, then
          * MIA, then the bar — and the bar option is the one that always
          * works, so a guest whose card keeps refusing is never cornered.
          */}
        {order.status === "failed" && !confirming && (
          <div className="mt-6">
            <div className="grid gap-2">
              {([["card", t.retryCard], ["mia", t.retryMia], ["cash", t.retryCash]] as const).map(
                ([method, label], i) => (
                  <button
                    key={method}
                    onClick={() => void payAgain(method)}
                    disabled={retrying !== null}
                    className={`tap flex items-center justify-center gap-3 px-6 ${
                      i === 0
                        ? "bg-clay-500 text-paper disabled:bg-dove-400"
                        : "border border-dove-400 bg-paper text-ink-muted disabled:opacity-50"
                    }`}
                    style={{ borderRadius: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-lg)" }}
                  >
                    {retrying === method ? t.retrying : i === 0 ? `${t.retry} · ${label}` : label}
                    {i === 0 && retrying === null && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
                        <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )
              )}
            </div>
            {retryNote && (
              <p role="alert" className="mt-3" style={{ color: "var(--color-danger)" }}>{retryNote}</p>
            )}
          </div>
        )}

        {/*
          * THE GUEST'S OWN WAY OUT — drawn for every UNPAID state and no other
          * (see UNPAID_STATES, and POST /orders/:id/cancel which re-checks all
          * of it server-side).
          *
          * THIS USED TO EXCLUDE 'awaiting_payment' and the reason given was "a
          * live bank page could still land". True, and it left a guest who had
          * changed their mind — or closed the bank tab, or been beaten to the
          * last bottle — with an order they could not pay for, could not
          * cancel, and could not get past on the menu. The answer was never to
          * cancel underneath the bank; it was to ASK THE BANK FIRST. The route
          * calls `POST /v2/checkouts/{id}/cancel` and cancels nothing unless
          * maib confirms the page is dead.
          *
          * Quiet styling on purpose: it is an exit, not the suggestion. The
          * suggestion is the button above it.
          */}
        {UNPAID_STATES.includes(order.status) && !confirming && (
          <div className="mt-6">
            <button
              onClick={() => void cancelOrder()}
              disabled={cancelling}
              className="tap w-full border border-dove-400 bg-paper px-6 text-ink-muted disabled:opacity-50"
              style={{ borderRadius: 0, fontFamily: "var(--font-display)" }}
            >
              {cancelArmed ? t.cancelConfirm : t.cancelOrder}
            </button>
            {cancelNote && (
              <p role="alert" className="mt-2" style={{ color: "var(--color-danger)" }}>{cancelNote}</p>
            )}
          </div>
        )}

        <ul className="m-0 mt-10 list-none border-t border-dove-200 p-0 text-left">
          {items.map((i, idx) => (
            <li key={idx} className="flex justify-between border-b border-dove-100 py-3">
              <span>{i.qty} × {i.name_snapshot}</span>
              <span className="tabular">{lei(i.line_total_minor)}</span>
            </li>
          ))}
        </ul>

        {order.tip_minor > 0 && (
          <div className="flex justify-between py-2 text-ink-muted">
            <span>{t.tip}</span><span className="tabular">{lei(order.tip_minor)}</span>
          </div>
        )}
        <div className="mt-2 flex justify-between" style={{ fontSize: "var(--text-lg)" }}>
          <span>{t.total}</span><span className="tabular">{lei(order.total_minor)}</span>
        </div>

        {/*
          * THE PROOF, FOR THE PERSON WHO HAS JUST PAID AND FOR THE BANK THAT
          * TOOK THE MONEY.
          *
          * This is the page the card rail redirects to, so an acquirer's
          * integration review reads it as the receipt: WHICH order, WHAT was
          * in it, WHEN it was paid, and WHO was paid. Three of those were
          * already here — the lines and the total are printed above, and the
          * pickup code is the largest thing on the screen — but the pickup
          * code is a word shouted across a garden, not a reference anyone can
          * look up, and nothing anywhere said the date.
          *
          * THE NUMBER, NOT THE ID. `order_number` is what the bar board, the
          * evening's report and the receipt email all print; the opaque id is
          * in the URL for anybody who needs it. Both tiers of the response
          * carry the number, so a guest who has lost their session still has
          * something to name at the counter.
          *
          * THE DATE COMES FROM `chisinauDate`/`chisinauTime` — the same two
          * functions the admin and the menu already use, so the whole product
          * writes the date and „21.30" the same way, in Europe/Chisinau, with
          * the dot the house writes its hours with. No second date format was
          * introduced for this.
          *
          * `lang` IS STILL PASSED, though the date is numeric now and the
          * digits of „10.08.2026" are the same in all three languages. The
          * argument earned its place when the month was spelled out — a
          * Russian guest once read „Оплачен 10 august 2026", a Romanian month
          * inside a Russian sentence — and it stays because the receipt email
          * passes `lang` into the Worker's twin of this function, and the two
          * calls should keep the same shape for whichever format the house
          * writes next.
          *
          * `paid_at` FIRST, `created_at` ONLY AS A FALLBACK, and they get
          * DIFFERENT WORDS. A cash order that nobody has settled has no paid
          * timestamp, and printing the moment it was placed under „Plătită la"
          * would make this page — the one page whose whole job is to be
          * believed — say something false.
          */}
        <div className="mt-6 border-t border-dove-100 pt-4 text-ink-faint" style={{ fontSize: "var(--text-kicker)" }}>
          <p>
            {t.orderNo} <span className="tabular">{order.order_number}</span>
          </p>
          {order.paid_at ? (
            <p className="mt-0.5">
              {t.paidAt}{" "}
              <span className="tabular">
                {chisinauDate(order.paid_at, lang)} · {chisinauTime(order.paid_at)}
              </span>
            </p>
          ) : order.created_at ? (
            <p className="mt-0.5">
              {t.placedAt}{" "}
              <span className="tabular">
                {chisinauDate(order.created_at, lang)} · {chisinauTime(order.created_at)}
              </span>
            </p>
          ) : null}

          {/*
            * WHO WAS PAID. Same block as the menu's footer, from the same
            * per-tenant settings, because the guest may have arrived here
            * straight from the bank and never seen the menu's footer at all.
            * Each line only if the house filled it in — no „IDNO" with nothing
            * after it, and no block at all for a house that has filled in none.
            */}
          {seller && (
            <div className="mt-3">
              <p className="uppercase" style={{ letterSpacing: "0.1em" }}>{t.seller}</p>
              {seller.name && <p className="mt-0.5 text-ink-muted">{seller.name}</p>}
              {seller.idno && (
                <p>{t.idno} <span className="tabular">{seller.idno}</span></p>
              )}
              {seller.address && <p>{seller.address}</p>}
            </div>
          )}
        </div>

        {backHref && (
          <a href={backHref} className="mt-10 inline-block text-clay-600 underline">{t.back}</a>
        )}
      </div>
    </div>
  );
}
